import { css } from 'styled-components';

export enum LayoutPosition {
    Absolute = 'absolute',
    Fixed = 'fixed',
    Relative = 'relative',
    Static = 'static',
    Sticky = 'sticky',
}

export type PositionCoverValue = LayoutPosition.Absolute | LayoutPosition.Fixed;

interface PositionCoverArgs {
    position?: PositionCoverValue;
    offset?: number;
}

function positionCover(args?: PositionCoverArgs) {
    const position = args?.position ?? LayoutPosition.Absolute;
    const offset = args?.offset ?? 0;

    return css`
        position: ${position};
        top: ${offset}px;
        right: ${offset}px;
        bottom: ${offset}px;
        left: ${offset}px;
        margin: auto;
    `;
}

interface ScrollbarCriteria {
    thin?: boolean;
    thumbColor?: string;
    trackColor?: string;
}

function scrollbar(args?: ScrollbarCriteria) {
    const thin = args?.thin !== false;
    const thumbColor = args?.thumbColor ?? 'rgba(255, 255, 255, 0.6)';
    const trackColor = args?.trackColor ?? 'rgba(0, 0, 0, 0.2)';

    return css`
        &::-webkit-scrollbar {
            // TODO: Clarify this magic value.
            width: 0.1875rem;
        }

        &::-webkit-scrollbar-thumb {
            background: ${thumbColor};
        }

        &::-webkit-scrollbar-track {
            background: ${trackColor};
        }

        scrollbar-width: ${thin ? 'thin' : 'auto'};
        scrollbar-color: ${thumbColor} ${trackColor};
    `;
}

interface FeatherMaskCriteria {
    featherSize?: number;
    scrollbarSize?: number;
}

function featherMask(args?: FeatherMaskCriteria) {
    const featherSize = args?.featherSize ?? 10;
    const scrollbarSize = args?.scrollbarSize ?? 10;

    // TODO: At the moment, this is a pretty specific type of
    // vertical feather mask. We should expand the API to allow
    // separate top/bottom sizes. We should also allow an option
    // to have a horizontal and/or vertical feather.

    return css`
        --mask-height: ${featherSize}px;
        --scrollbar-width-offset: ${scrollbarSize}px;

        --mask-image-content: linear-gradient(
            transparent,
            black var(--mask-height),
            black calc(100% - var(--mask-height)),
            transparent
        );
        --mask-image-scrollbar: linear-gradient(black, black);

        --mask-size-content: calc(100% - var(--scrollbar-width-offset)) 100%;
        --mask-size-scrollbar: var(--scrollbar-width-offset) 100%;

        mask-image: var(--mask-image-content), var(--mask-image-scrollbar);
        mask-size: var(--mask-size-content), var(--mask-size-scrollbar);
        mask-position:
            0 0,
            100% 0;
        mask-repeat: no-repeat, no-repeat;
    `;
}

const fullWidthFix = css`
    min-width: 0;
    max-width: 100%;
`;

const fullHeightFix = css`
    min-height: 0;
    max-height: 100%;
`;

const visuallyHidden = css`
    clip: rect(0 0 0 0);
    position: absolute;
    overflow: hidden;
    margin: -1px;
    padding: 0;
    width: 1px;
    height: 1px;
    border: 0;
`;

function visible(visible = true) {
    return visible
        ? css`
              opacity: 1;
              visibility: visible;
              pointer-events: auto;
          `
        : css`
              opacity: 0;
              visibility: hidden;
              pointer-events: none;
          `;
}

export const styledLayout = {
    positionCover,
    scrollbar,
    featherMask,
    fullWidthFix,
    fullHeightFix,
    visuallyHidden,
    visible,
};
